import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { AgentFolder, AgentFolderTreeNode } from "@/services/agentFoldersService";
import { Folder, FolderPlus, MoreHorizontal, Pencil, Share2, Trash2, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface AgentFoldersBarProps {
  agentFolderTree: AgentFolderTreeNode[];
  selectedAgentFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  onCreateFolder: () => void;
  onCreateSubfolder?: (parentId: string) => void;
  onRenameFolder: (folder: AgentFolder) => void;
  onShareFolder: (folder: AgentFolder) => void;
  onDeleteFolder: (folderId: string) => void;
}

function FolderTreeButtons({
  nodes,
  depth,
  selectedAgentFolderId,
  onSelectFolder,
  onRenameFolder,
  onShareFolder,
  onDeleteFolder,
  onCreateSubfolder,
}: {
  nodes: AgentFolderTreeNode[];
  depth: number;
  selectedAgentFolderId: string | null;
  onSelectFolder: (folderId: string | null) => void;
  onRenameFolder: (folder: AgentFolder) => void;
  onShareFolder: (folder: AgentFolder) => void;
  onDeleteFolder: (folderId: string) => void;
  onCreateSubfolder?: (parentId: string) => void;
}) {
  return (
    <>
      {nodes.map((folder) => (
        <div key={folder.id}>
          <div
            className="flex items-center gap-0.5"
            style={{ marginLeft: depth * 12 }}
          >
            {folder.children.length > 0 ? (
              <ChevronRight className="h-3 w-3 text-muted-foreground shrink-0" />
            ) : (
              <span className="w-3 shrink-0" />
            )}
            <Button
              variant={selectedAgentFolderId === folder.id ? "secondary" : "outline"}
              size="sm"
              onClick={() => onSelectFolder(folder.id)}
              className="gap-1"
            >
              <Folder className="h-3.5 w-3.5" />
              {folder.name}
              <span className="text-xs text-muted-foreground">({folder.agentCount})</span>
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-7 w-7">
                  <MoreHorizontal className="h-3.5 w-3.5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                {onCreateSubfolder && (
                  <DropdownMenuItem onClick={() => onCreateSubfolder(folder.id)}>
                    <FolderPlus className="h-4 w-4 mr-2" />
                    New subfolder
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onRenameFolder(folder)}>
                  <Pencil className="h-4 w-4 mr-2" />
                  Rename
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={folder.agentCount === 0}
                  onClick={() => onShareFolder(folder)}
                >
                  <Share2 className="h-4 w-4 mr-2" />
                  {folder.agentCount === 0 ? "Share folder (add agents first)" : "Share folder"}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => onDeleteFolder(folder.id)}
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete folder
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {folder.children.length > 0 && (
            <FolderTreeButtons
              nodes={folder.children}
              depth={depth + 1}
              selectedAgentFolderId={selectedAgentFolderId}
              onSelectFolder={onSelectFolder}
              onRenameFolder={onRenameFolder}
              onShareFolder={onShareFolder}
              onDeleteFolder={onDeleteFolder}
              onCreateSubfolder={onCreateSubfolder}
            />
          )}
        </div>
      ))}
    </>
  );
}

export function AgentFoldersBar({
  agentFolderTree,
  selectedAgentFolderId,
  onSelectFolder,
  onCreateFolder,
  onCreateSubfolder,
  onRenameFolder,
  onShareFolder,
  onDeleteFolder,
}: AgentFoldersBarProps) {
  return (
    <div className={cn("flex flex-wrap items-start gap-2")}>
      <Button
        variant="outline"
        size="sm"
        className="gap-1"
        onClick={onCreateFolder}
        data-testid="button-create-agent-folder-inline"
      >
        <FolderPlus className="h-3.5 w-3.5" />
        New Folder
      </Button>
      <Button
        variant={selectedAgentFolderId === null ? "secondary" : "outline"}
        size="sm"
        onClick={() => onSelectFolder(null)}
      >
        All agents
      </Button>
      <FolderTreeButtons
        nodes={agentFolderTree}
        depth={0}
        selectedAgentFolderId={selectedAgentFolderId}
        onSelectFolder={onSelectFolder}
        onRenameFolder={onRenameFolder}
        onShareFolder={onShareFolder}
        onDeleteFolder={onDeleteFolder}
        onCreateSubfolder={onCreateSubfolder}
      />
    </div>
  );
}
